import {Injectable} from '@angular/core';

type Store = { [token: string]: any; };

@Injectable({
    providedIn: 'root'
})
export class LocalStorageService {

    private _store: Store = {};

    // The availability of the local storage.
    private readonly _available: boolean = false;

    constructor() {
        // Validate if the localstorage is available.
        this._available = this.isAvailable();
    }

    get<T = any>(token: string) {
        if (!this._available) {
            const value = this._store[token];
            return value === undefined
                ? null
                : value;
        }

        // Retrieve the raw data from the local storage.
        const rawData = localStorage.getItem(token);

        // Try to parse the data.
        let parsedData: T | null = null;

        // Only parse the data when it is not null.
        if (rawData !== null) {
            try {
                parsedData = JSON.parse(rawData);
            } catch (error) {
                throw new Error('The raw data cannot be parsed; Invalid data in the storage.');
            }
        }

        // Return the parsed data.
        return parsedData;
    }

    set<T = any>(token: string, data: T) {
        if (!this._available) {
            this._store[token] = data;
            return;
        }

        // Stringify the data.
        const rawData = JSON.stringify(data);

        // Set the raw data in the storage.
        return localStorage.setItem(token, rawData);
    }

    remove(token: string) {
        if (!this._available) {
            delete this._store[token];
            return;
        }

        return localStorage.removeItem(token);
    }

    clear() {
        // If available clear the local storage.
        if (this._available) {
            return localStorage.clear();
        }

        // Else reset the store object.
        this._store = {};
    }

    private isAvailable = () => {
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.setItem('feature_test', 'yes');
                if (localStorage.getItem('feature_test') === 'yes') {
                    localStorage.removeItem('feature_test');
                    return true;
                } else {
                    return false;
                }
            } catch {
                return false;
            }
        } else {
            return false;
        }
    };

}
